import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    useMediaQuery,
    useTheme,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    CircularProgress,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { theme as appTheme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { updateGlobalConfiguration, getGlobalConfig } from '../../redux/configuration/action';
import { RootState } from '../../redux/root-reducer';
import { IGlobalConfigurationUpdate } from '../../redux/configuration/types';
import { countries } from '../../utils/countriesList';

interface GlobalConfigurationModalProps {
    open: boolean;
    onClose: () => void;
}

const GlobalConfigurationModal: React.FC<GlobalConfigurationModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const globalConfiguration = useSelector((state: RootState) => state.configuration.globalConfiguration);
    const updatingConfig = useSelector((state: RootState) => state.configuration.updatingConfig);
    const fetchingGlobalConfig = useSelector((state: RootState) => state.configuration.fetchingGlobalConfig);

    const [config, setConfig] = useState<IGlobalConfigurationUpdate>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string>('NG'); // Default to Nigeria

    // Load global configuration if not already loaded
    useEffect(() => {
        if (open && !globalConfiguration && !fetchingGlobalConfig) {
            dispatch(getGlobalConfig());
        }
    }, [open, globalConfiguration, fetchingGlobalConfig, dispatch]);

    useEffect(() => {
        if (globalConfiguration) {
            // Create a deep copy of the configuration to avoid modifying the original
            const deepCopy = JSON.parse(JSON.stringify(globalConfiguration));
            setConfig(deepCopy);
        }
    }, [globalConfiguration]);

    // Initialize config when modal opens
    useEffect(() => {
        if (open && globalConfiguration) {
            // Create a deep copy of the configuration to avoid modifying the original
            const deepCopy = JSON.parse(JSON.stringify(globalConfiguration));
            setConfig(deepCopy);
            setHasChanges(false);
            setIsSaving(false);
        }
    }, [open, globalConfiguration]);

    // Close modal when update is successful
    useEffect(() => {
        if (!updatingConfig && isSaving && hasChanges && globalConfiguration) {
            // Only close the modal when we're not in the middle of updating
            // and the update was successful (updatingConfig went from true to false)
            // and we were actually saving (not just toggling)
            setHasChanges(false);
            setIsSaving(false);
            // Close the modal after successful save
            onClose();
        }
    }, [updatingConfig, globalConfiguration, onClose, isSaving, hasChanges]);

    // Reset config when modal closes
    useEffect(() => {
        if (!open) {
            setHasChanges(false);
            setIsSaving(false);
            if (globalConfiguration) {
                // Create a deep copy of the configuration to avoid modifying the original
                const deepCopy = JSON.parse(JSON.stringify(globalConfiguration));
                setConfig(deepCopy);
            }
        }
    }, [open, globalConfiguration]);

    // Check if there are any changes by comparing config with globalConfiguration
    useEffect(() => {
        if (globalConfiguration && Object.keys(config).length > 0) {
            // Use a more efficient comparison that doesn't trigger on every render
            const hasAnyChanges = JSON.stringify(config) !== JSON.stringify(globalConfiguration);
            if (hasAnyChanges !== hasChanges) {
                setHasChanges(hasAnyChanges);
            }
        }
    }, [config, globalConfiguration, hasChanges]);

    // Show loading state if no configuration is available
    if (!globalConfiguration && !fetchingGlobalConfig) {
        return null;
    }

    // Show loading state while fetching
    if (fetchingGlobalConfig) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={isMobile}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                        backgroundImage: 'none',
                        minHeight: isMobile ? '100vh' : 600,
                        maxHeight: isMobile ? '100vh' : '90vh',
                        borderRadius: isMobile ? 0 : 2,
                    },
                }}>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: appTheme[themeStyle].highlight }} />
                </DialogContent>
            </Dialog>
        );
    }

    // Ensure config is properly initialized
    if (!config || Object.keys(config).length === 0) {
        return null;
    }

    // Helper function to safely get nested object values
    const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    // Helper function to safely set nested object values
    const setNestedValue = (obj: any, path: string, value: any) => {
        const keys = path.split('.');
        let current = obj;

        // Navigate to the parent object, creating missing objects as we go
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
                current[keys[i]] = {};
            }
            current = current[keys[i]];
        }

        // Set the value at the final key
        current[keys[keys.length - 1]] = value;
    };

    const handleToggle = (path: string, value?: 'active' | 'inactive' | boolean | number) => {
        const newConfig = { ...config };

        // Get the current value from the current config (not the original)
        const currentValue = getNestedValue(newConfig, path);

        // If a specific value is provided, use it
        if (value !== undefined) {
            setNestedValue(newConfig, path, value);
        } else {
            // For toggle switches, switch between active/inactive or true/false
            let newValue;
            if (typeof currentValue === 'boolean') {
                newValue = !currentValue;
            } else if (currentValue === 'active' || currentValue === 'inactive') {
                newValue = currentValue === 'active' ? 'inactive' : 'active';
            } else {
                // Default to active if no current value
                newValue = 'active';
            }
            setNestedValue(newConfig, path, newValue);
        }

        setConfig(newConfig);
        setHasChanges(true);
    };

    // Helper function to convert bytes to MB for display
    const formatBytesToMB = (bytes: number) => {
        return (bytes / (1024 * 1024)).toFixed(2);
    };

    // Helper function to format bytes to readable format (B, KB, MB, GB)
    const formatBytesReadable = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Helper function to convert MB to bytes for saving
    const parseMBToBytes = (mb: string) => {
        const num = parseFloat(mb);
        return isNaN(num) ? 0 : Math.round(num * 1024 * 1024);
    };

    // Helper function to handle number changes for non-byte fields
    const handleNumberChange = (path: string, value: string) => {
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
            handleToggle(path, numValue);
        }
    };

    const formatPrice = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const parsePrice = (dollars: string) => {
        const num = parseFloat(dollars.replace('$', ''));
        return isNaN(num) ? 0 : Math.round(num * 100);
    };

    const handleCountryChange = (countryCode: string) => {
        setSelectedCountry(countryCode);
        setConfig({});
        setHasChanges(false);
        setIsSaving(false);
        dispatch(getGlobalConfig(countryCode));
    };

    const handleSave = () => {
        if (!hasChanges) return;

        setIsSaving(true);

        // Save the entire configuration object, not just changed fields
        dispatch(updateGlobalConfiguration(config, selectedCountry));
    };

    const renderToggleSection = (title: string, items: Array<{ key: string; label: string; path: string }>) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {items.map(({ key, label, path }) => {
                        const currentValue = getNestedValue(config, path);
                        return (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Switch
                                        checked={currentValue === 'active' || currentValue === true}
                                        onChange={() => handleToggle(path)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: appTheme[themeStyle].highlight,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: appTheme[themeStyle].highlight,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {label}
                                    </Typography>
                                }
                            />
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    const renderNumberSectionWithDescription = (
        title: string,
        items: Array<{
            key: string;
            label: string;
            path: string;
            description?: string;
            isPrice?: boolean;
            isBytes?: boolean;
            isSlider?: boolean;
        }>,
    ) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={3}>
                    {items.map(({ key, label, path, description, isPrice, isBytes, isSlider }) => {
                        const currentValue = getNestedValue(config, path);
                        let displayValue: string;

                        if (isPrice) {
                            displayValue = formatPrice(currentValue || 0);
                        } else if (isBytes) {
                            displayValue = formatBytesToMB(currentValue || 0);
                        } else {
                            displayValue = (currentValue || 0).toString();
                        }

                        return (
                            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            minWidth: 200,
                                        }}>
                                        {label}
                                    </Typography>
                                    <TextField
                                        value={displayValue}
                                        onChange={e => {
                                            if (isPrice) {
                                                const cents = parsePrice(e.target.value);
                                                handleToggle(path, cents);
                                            } else if (isBytes) {
                                                const bytes = parseMBToBytes(e.target.value);
                                                handleToggle(path, bytes);
                                            } else {
                                                handleNumberChange(path, e.target.value);
                                            }
                                        }}
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: appTheme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: appTheme[themeStyle].form_divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: appTheme[themeStyle].highlight,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: appTheme[themeStyle].grey,
                                            },
                                        }}
                                    />
                                    {isBytes && (
                                        <Typography
                                            sx={{
                                                color: appTheme[themeStyle].grey,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            MB
                                        </Typography>
                                    )}
                                </Box>
                                {description && (
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.75rem',
                                            fontStyle: 'italic',
                                            ml: 0,
                                            maxWidth: '600px',
                                        }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    const renderNumberSection = (
        title: string,
        items: Array<{
            key: string;
            label: string;
            path: string;
            isPrice?: boolean;
            isBytes?: boolean;
            isSlider?: boolean;
        }>,
    ) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {items.map(({ key, label, path, isPrice, isBytes, isSlider }) => {
                        const currentValue = getNestedValue(config, path);
                        let displayValue: string;

                        if (isPrice) {
                            displayValue = formatPrice(currentValue || 0);
                        } else if (isBytes) {
                            displayValue = formatBytesToMB(currentValue || 0);
                        } else {
                            displayValue = (currentValue || 0).toString();
                        }

                        if (isSlider) {
                            // Render slider for file size fields
                            return (
                                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {label}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Slider
                                            value={currentValue || 0}
                                            onChange={(_, value) => handleToggle(path, value as number)}
                                            min={0}
                                            max={10 * 1024 * 1024 * 1024} // 10GB in bytes
                                            step={1024 * 1024} // 1MB steps
                                            sx={{
                                                flex: 1,
                                                '& .MuiSlider-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSlider-rail': {
                                                    backgroundColor: appTheme[themeStyle].form_divider,
                                                },
                                                '& .MuiSlider-thumb': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    '&:hover': {
                                                        backgroundColor: appTheme[themeStyle].highlight,
                                                        opacity: 0.8,
                                                    },
                                                    '&.Mui-focusVisible': {
                                                        boxShadow: `0 0 0 8px ${appTheme[themeStyle].highlight}20`,
                                                    },
                                                },
                                                '& .MuiSlider-valueLabel': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    color: appTheme[themeStyle].pure_black,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                },
                                                '& .MuiSlider-mark': {
                                                    backgroundColor: appTheme[themeStyle].form_divider,
                                                },
                                                '& .MuiSlider-markLabel': {
                                                    color: appTheme[themeStyle].grey,
                                                    fontSize: '0.75rem',
                                                },
                                            }}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={value => formatBytesReadable(value)}
                                            marks={[
                                                { value: 0, label: '0 B' },
                                                { value: 1024 * 1024 * 1024, label: '1 GB' },
                                                { value: 5 * 1024 * 1024 * 1024, label: '5 GB' },
                                                { value: 10 * 1024 * 1024 * 1024, label: '10 GB' },
                                            ]}
                                        />
                                        <Typography
                                            sx={{
                                                color: appTheme[themeStyle].grey,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                                minWidth: '80px',
                                                textAlign: 'right',
                                            }}>
                                            {formatBytesToMB(currentValue || 0)} MB
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }

                        // Render regular text input for non-slider fields
                        return (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                    sx={{
                                        color: appTheme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        minWidth: 200,
                                    }}>
                                    {label}
                                </Typography>
                                <TextField
                                    value={displayValue}
                                    onChange={e => {
                                        if (isPrice) {
                                            const cents = parsePrice(e.target.value);
                                            handleToggle(path, cents);
                                        } else if (isBytes) {
                                            const bytes = parseMBToBytes(e.target.value);
                                            handleToggle(path, bytes);
                                        } else {
                                            handleNumberChange(path, e.target.value);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: appTheme[themeStyle].tag_white,
                                            '& fieldset': {
                                                borderColor: appTheme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: appTheme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: appTheme[themeStyle].grey,
                                        },
                                    }}
                                />
                                {isBytes && (
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                        }}>
                                        MB
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                    backgroundImage: 'none',
                    minHeight: isMobile ? '100vh' : 600,
                    maxHeight: isMobile ? '100vh' : '90vh',
                    borderRadius: isMobile ? 0 : 2,
                },
            }}>
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    color: appTheme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    borderBottom: `1px solid ${appTheme[themeStyle].form_divider}`,
                    position: 'relative',
                    pr: 6,
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                }}>
                Global Configuration
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: appTheme[themeStyle].tag_white,
                        '&:hover': {
                            backgroundColor: appTheme[themeStyle].button_background,
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack spacing={3}>
                    {/* Country Selector */}
                    <Box sx={{ my: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel
                                sx={{
                                    color: appTheme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '&.Mui-focused': {
                                        color: appTheme[themeStyle].highlight,
                                    },
                                }}>
                                Select Country
                            </InputLabel>
                            <Select
                                value={selectedCountry}
                                label="Select Country"
                                onChange={e => handleCountryChange(e.target.value)}
                                disabled={fetchingGlobalConfig || updatingConfig}
                                sx={{
                                    color: appTheme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].form_divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].highlight,
                                    },
                                    '& .MuiSelect-icon': {
                                        color: appTheme[themeStyle].tag_white,
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                                            color: appTheme[themeStyle].tag_white,
                                            maxHeight: 300,
                                        },
                                    },
                                }}>
                                {countries.map(country => (
                                    <MenuItem
                                        key={country.code}
                                        value={country.code}
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                backgroundColor: appTheme[themeStyle].button_background,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: appTheme[themeStyle].highlight,
                                                color: appTheme[themeStyle].pure_black,
                                                '&:hover': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    opacity: 0.9,
                                                },
                                            },
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                                            <Typography>{country.name}</Typography>
                                            <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: '0.875rem' }}>
                                                ({country.code})
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {fetchingGlobalConfig ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: appTheme[themeStyle].highlight }} />
                        </Box>
                    ) : (
                        <>
                            {renderToggleSection('Audition Filters', [
                                {
                                    key: 'rehearsal_date',
                                    label: 'Rehearsal Date',
                                    path: 'audition_filters.rehearsal_date',
                                },
                                {
                                    key: 'required_media',
                                    label: 'Required Media',
                                    path: 'audition_filters.required_media',
                                },
                                { key: 'age_range', label: 'Age Range', path: 'audition_filters.age_range' },
                                {
                                    key: 'filming_location',
                                    label: 'Filming Location',
                                    path: 'audition_filters.filming_location',
                                },
                                { key: 'compensation', label: 'Compensation', path: 'audition_filters.compensation' },
                                {
                                    key: 'audition_type',
                                    label: 'Audition Type',
                                    path: 'audition_filters.audition_type',
                                },
                                {
                                    key: 'required_skill',
                                    label: 'Required Skill',
                                    path: 'audition_filters.required_skill',
                                },
                                { key: 'tags', label: 'Tags', path: 'audition_filters.tags' },
                                {
                                    key: 'employment_type',
                                    label: 'Employment Type',
                                    path: 'audition_filters.employment_type',
                                },
                            ])}

                            {renderToggleSection('Talent Filters', [
                                { key: 'location', label: 'Location', path: 'talent_filter.location' },
                                { key: 'age_range', label: 'Age Range', path: 'talent_filter.age_range' },
                                { key: 'height', label: 'Height', path: 'talent_filter.height' },
                                { key: 'skills', label: 'Skills', path: 'talent_filter.skills' },
                                { key: 'sex', label: 'Sex', path: 'talent_filter.sex' },
                                { key: 'hair_color', label: 'Hair Color', path: 'talent_filter.hair_color' },
                                { key: 'body_type', label: 'Body Type', path: 'talent_filter.body_type' },
                                { key: 'weight', label: 'Weight', path: 'talent_filter.weight' },
                                {
                                    key: 'language_spoken',
                                    label: 'Language Spoken',
                                    path: 'talent_filter.language_spoken',
                                },
                                {
                                    key: 'special_skills',
                                    label: 'Special Skills',
                                    path: 'talent_filter.special_skills',
                                },
                            ])}

                            {renderToggleSection('Features', [
                                { key: 'voting', label: 'Voting', path: 'features.voting' },
                                { key: 'movie_ticket', label: 'Movie Ticket', path: 'features.movie_ticket' },
                                { key: 'contest_emails', label: 'Contest Emails', path: 'features.contest_emails' },
                                { key: 'audition', label: 'Audition', path: 'features.audition' },
                                { key: 'campaigns', label: 'Campaigns', path: 'features.campaigns' },
                                { key: 'buzzbridge', label: 'BuzzBridge', path: 'features.buzzbridge' },
                                { key: 'discover', label: 'Discover', path: 'features.discover' },
                                { key: 'events', label: 'Events', path: 'features.events' },
                                { key: 'talents', label: 'Talents', path: 'features.talents' },
                                { key: 'feed', label: 'Feed', path: 'features.feed' },
                                { key: 'ads', label: 'Ads', path: 'features.ads' },
                                { key: 'hls_play', label: 'HLS Play', path: 'features.hls_play' },
                                { key: 'acts', label: 'Acts', path: 'features.acts' },
                            ])}

                            {renderToggleSection('Account Features', [
                                { key: 'movies', label: 'Movies', path: 'features.account_features.movies' },
                                {
                                    key: 'productions',
                                    label: 'Productions',
                                    path: 'features.account_features.productions',
                                },
                                {
                                    key: 'contributions',
                                    label: 'Contributions',
                                    path: 'features.account_features.contributions',
                                },
                                { key: 'profile', label: 'Profile', path: 'features.account_features.profile' },
                                { key: 'reviews', label: 'Reviews', path: 'features.account_features.reviews' },
                                {
                                    key: 'applications',
                                    label: 'Applications',
                                    path: 'features.account_features.applications',
                                },
                                { key: 'settings', label: 'Settings', path: 'features.account_features.settings' },
                                { key: 'people', label: 'People', path: 'features.account_features.people' },
                                { key: 'tickets', label: 'Tickets', path: 'features.account_features.tickets' },
                                { key: 'likes', label: 'Likes', path: 'features.account_features.likes' },
                                {
                                    key: 'boxofix_score',
                                    label: 'Boxofix Score',
                                    path: 'features.account_features.boxofix_score',
                                },
                                {
                                    key: 'subscriptions',
                                    label: 'Subscriptions',
                                    path: 'features.account_features.subscriptions',
                                },
                            ])}

                            {renderNumberSection('Limits', [
                                {
                                    key: 'default_casting_limit',
                                    label: 'Default Casting Limit',
                                    path: 'default_casting_limit',
                                },
                                {
                                    key: 'default_application_limit',
                                    label: 'Default Application Limit',
                                    path: 'default_application_limit',
                                },
                                { key: 'act_limit', label: 'Act Limit', path: 'act_limit' },
                                {
                                    key: 'contest_submission_limit',
                                    label: 'Contest Submission Limit',
                                    path: 'contest_submission_limit',
                                },
                            ])}

                            {renderNumberSection('Ticket Pricing (in dollars)', [
                                {
                                    key: 'min_ticket_price',
                                    label: 'Minimum Ticket Price',
                                    path: 'min_ticket_price',
                                    isPrice: true,
                                },
                                {
                                    key: 'ticket_flat_fee',
                                    label: 'Ticket Flat Fee',
                                    path: 'ticket_flat_fee',
                                    isPrice: true,
                                },
                                {
                                    key: 'ticket_additional_fee',
                                    label: 'Ticket Additional Fee (%)',
                                    path: 'ticket_additional_fee',
                                },
                                {
                                    key: 'max_ticket_price',
                                    label: 'Maximum Ticket Price',
                                    path: 'max_ticket_price',
                                    isPrice: true,
                                },
                            ])}

                            {renderNumberSection('Submission Pricing (in dollars)', [
                                {
                                    key: 'min_submission_price',
                                    label: 'Minimum Submission Price',
                                    path: 'min_submission_price',
                                    isPrice: true,
                                },
                                {
                                    key: 'submission_flat_fee',
                                    label: 'Submission Flat Fee',
                                    path: 'submission_flat_fee',
                                    isPrice: true,
                                },
                                {
                                    key: 'added_percentage_per_submission',
                                    label: 'Added Percentage per Submission (%)',
                                    path: 'added_percentage_per_submission',
                                },
                                {
                                    key: 'max_submission_price',
                                    label: 'Maximum Submission Price',
                                    path: 'max_submission_price',
                                    isPrice: true,
                                },
                            ])}

                            {renderNumberSection('Campaign Pricing (in dollars)', [
                                {
                                    key: 'campaign_max',
                                    label: 'Campaign Maximum Amount',
                                    path: 'campaign_max',
                                    isPrice: true,
                                },
                                {
                                    key: 'campaign_percentage',
                                    label: 'Campaign Percentage (%)',
                                    path: 'campaign_percentage',
                                },
                                {
                                    key: 'campaign_flat_fee',
                                    label: 'Campaign Flat Fee',
                                    path: 'campaign_flat_fee',
                                    isPrice: true,
                                },
                            ])}

                            {renderToggleSection('BuzzBridge Auto-Link', [
                                { key: 'instagram', label: 'Instagram', path: 'buzzbridge.enableAutoLink.Instagram' },
                                { key: 'youtube', label: 'YouTube', path: 'buzzbridge.enableAutoLink.YouTube' },
                                { key: 'tiktok', label: 'TikTok', path: 'buzzbridge.enableAutoLink.TikTok' },
                            ])}

                            {renderToggleSection('System Settings', [
                                { key: 'use_cache', label: 'Use Cache', path: 'use_cache' },
                            ])}

                            {renderNumberSection('Cache Settings', [
                                { key: 'cache_duration', label: 'Cache Duration (ms)', path: 'cache_duration' },
                            ])}

                            {renderNumberSection('Media Control', [
                                {
                                    key: 'file_size_limit',
                                    label: 'File Size Limit',
                                    path: 'media_control.file_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'mobile_size_limit',
                                    label: 'Mobile Size Limit',
                                    path: 'media_control.mobile_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'image_size_limit',
                                    label: 'Image Size Limit',
                                    path: 'media_control.image_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'pagination_count',
                                    label: 'Pagination Count',
                                    path: 'media_control.pagination_count',
                                },
                            ])}

                            {renderNumberSectionWithDescription('Ad Control', [
                                {
                                    key: 'skipped',
                                    label: 'Videos after Skipped Ad',
                                    path: 'ad_control.skipped',
                                    description:
                                        'Number of videos to play before showing another ad if the user skipped the last ad',
                                },
                                {
                                    key: 'ended',
                                    label: 'Videos after Completed Ad',
                                    path: 'ad_control.ended',
                                    description:
                                        'Number of videos to play before showing another ad if the user completed the last ad',
                                },
                                {
                                    key: 'start',
                                    label: 'Videos after Started Ad',
                                    path: 'ad_control.start',
                                    description:
                                        'Number of videos to play before showing another ad if the user only started the last ad',
                                },
                            ])}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: `1px solid ${appTheme[themeStyle].form_divider}` }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: appTheme[themeStyle].grey,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!hasChanges || updatingConfig || fetchingGlobalConfig || isSaving}
                    variant="contained"
                    sx={{
                        backgroundColor: appTheme[themeStyle].highlight,
                        color: appTheme[themeStyle].pure_black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': {
                            backgroundColor: appTheme[themeStyle].highlight,
                            opacity: 0.9,
                        },
                        '&:disabled': {
                            backgroundColor: appTheme[themeStyle].grey,
                            color: appTheme[themeStyle].deep_grey,
                        },
                    }}>
                    {updatingConfig || fetchingGlobalConfig || isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GlobalConfigurationModal;
